import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getProjectBySlug } from '../data/projects';
import { useLanguage } from '../i18n/LanguageContext';

const socialImage =
  'https://media-manager-c.questera.ai/greta-media/b64cc446cdd47675bdad19de596fda49c979fcc46f742bafa38ab384e40234c3cc3101a26c56d32c5dccb06920b38db8/images/aW1hZ2UvanBlZw==/695512a80eca3f037dfcb7c38c0f1b87.jpg';

const setMeta = (attribute, key, value) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', value);
};

const normalizeTitle = (value) => {
  const title = value.trim();
  const expanded =
    title.length < 30 ? `${title} | Dervis Baygül Portfolio` : title;
  return expanded.length > 60 ? `${expanded.slice(0, 57).trimEnd()}...` : expanded;
};

const normalizeDescription = (value, language) => {
  const suffixes = {
    de: ' Dervis Baygül entwickelt digitale Produkte und technische Lösungen.',
    en: ' Dervis Baygül builds digital products and technical solutions.',
    tr: ' Dervis Baygül dijital ürünler ve teknik çözümler geliştirir.'
  };
  const description = value.replace(/\s+/g, ' ').trim();
  const expanded =
    description.length < 120
      ? `${description}${suffixes[language] || suffixes.de}`
      : description;

  return expanded.length > 160
    ? `${expanded.slice(0, 157).trimEnd()}...`
    : expanded;
};

const RouteMetadata = () => {
  const location = useLocation();
  const { lang, localize } = useLanguage();

  useEffect(() => {
    const projectMatch = location.pathname.match(/^\/projekte\/([^/]+)$/);
    const project = projectMatch
      ? getProjectBySlug(decodeURIComponent(projectMatch[1]))
      : null;

    const localizedProject = project ? localize(project) : null;
    const pageMetadata = {
      '/': {
        title: {
          de: 'Dervis Baygül | Tech-Unternehmer & Entwickler',
          en: 'Dervis Baygül | Technology Entrepreneur',
          tr: 'Dervis Baygül | Teknoloji Girişimcisi'
        },
        description: {
          de: 'Dervis Baygül entwickelt Apps, Webseiten und KI-Telefonassistenten und bietet Elektroarbeiten, Solaranlagen sowie ChargeNest-Powerbank-Stationen.',
          en: 'Dervis Baygül builds apps, websites and AI phone assistants, and offers electrical work, solar installations and ChargeNest power bank stations.',
          tr: 'Dervis Baygül uygulamalar, web siteleri ve yapay zekâlı telefon asistanları geliştirir; elektrik işleri ve güneş enerjisi çözümleri sunar.'
        }
      },
      '/impressum': {
        title: {
          de: 'Impressum – Dervis Baygül Portfolio',
          en: 'Legal Notice – Dervis Baygül Portfolio',
          tr: 'Yasal Bilgiler – Dervis Baygül Portfolio'
        },
        description: {
          de: 'Impressum von Dervis Baygül mit Anbieterkennzeichnung, Anschrift in Offenbach und Kontaktinformationen für bayguel.de.',
          en: 'Legal notice for bayguel.de with provider details, business address in Offenbach and contact information for Dervis Baygül.',
          tr: 'bayguel.de için Dervis Baygül iletişim bilgileri, Offenbach adresi ve yasal sağlayıcı bilgilerini içeren yasal bilgilendirme.'
        }
      },
      '/datenschutz': {
        title: {
          de: 'Datenschutz – Dervis Baygül Website',
          en: 'Privacy – Dervis Baygül Website',
          tr: 'Gizlilik – Dervis Baygül Web Sitesi'
        },
        description: {
          de: 'Datenschutzhinweise für bayguel.de: Informationen zur Verarbeitung personenbezogener Daten, zum Kontaktformular und zum Hosting der Website.',
          en: 'Privacy information for bayguel.de covering personal data processing, the contact form and website hosting.',
          tr: 'bayguel.de gizlilik bilgileri; kişisel verilerin işlenmesi, iletişim formu ve web sitesi barındırması hakkında açıklamalar.'
        }
      }
    };

    const metadata = pageMetadata[location.pathname];
    const title = localizedProject
      ? `${localizedProject.title} | Dervis Baygül`
      : metadata?.title[lang] || metadata?.title.de;
    const description = localizedProject
      ? localizedProject.summary
      : metadata?.description[lang] || metadata?.description.de;

    document.title = normalizeTitle(title);
    setMeta('name', 'title', normalizeTitle(title));
    setMeta('name', 'description', normalizeDescription(description, lang));
    setMeta('property', 'og:title', normalizeTitle(title));
    setMeta('property', 'og:description', normalizeDescription(description, lang));
    setMeta('property', 'og:image', socialImage);
    setMeta('property', 'og:url', window.location.href);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', normalizeTitle(title));
    setMeta('name', 'twitter:description', normalizeDescription(description, lang));
    setMeta('name', 'twitter:image', socialImage);
    setMeta('name', 'twitter:url', window.location.href);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.href;
  }, [lang, localize, location.pathname]);

  return null;
};

export default RouteMetadata;
